import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import {
  getStoredUserAvatar,
  getUserInitials,
  removeStoredUserAvatar,
  saveStoredUserAvatar,
  USER_AVATAR_UPDATED_EVENT
} from '@/lib/userAvatar';
import { Avatar } from '@/components/ui/avatar';

interface UserAvatarProps {
  identifier?: string | null;
  displayName?: string | null;
  providerImage?: unknown;
  className?: string;
  fallbackClassName?: string;
}

function validProviderImage(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const source = value.trim();
  return source.startsWith('https://') || source.startsWith('data:image/')
    ? source
    : null;
}

export function useUserAvatar(
  identifier?: string | null,
  providerImage?: unknown
) {
  const [storedImage, setStoredImage] = useState<string | null>(() =>
    getStoredUserAvatar(identifier)
  );

  useEffect(() => {
    const refresh = () => setStoredImage(getStoredUserAvatar(identifier));
    refresh();
    window.addEventListener(USER_AVATAR_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(USER_AVATAR_UPDATED_EVENT, refresh);
  }, [identifier]);

  return {
    source: storedImage || validProviderImage(providerImage),
    hasCustomImage: Boolean(storedImage),
    saveImage: (dataUrl: string) => {
      if (identifier) saveStoredUserAvatar(identifier, dataUrl);
    },
    removeImage: () => {
      if (identifier) removeStoredUserAvatar(identifier);
    }
  };
}

export default function UserAvatar({
  identifier,
  displayName,
  providerImage,
  className,
  fallbackClassName
}: UserAvatarProps) {
  const { source } = useUserAvatar(identifier, providerImage);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => setImageFailed(false), [source]);

  return (
    <Avatar
      className={cn(
        'relative border border-outline bg-surface/70 shadow-sm',
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-0 flex items-center justify-center bg-primary/10 font-semibold text-primary',
          fallbackClassName
        )}
      >
        {getUserInitials(displayName, identifier)}
      </span>
      {source && !imageFailed ? (
        <img
          src={source}
          alt=""
          referrerPolicy="no-referrer"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : null}
    </Avatar>
  );
}

